// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '../common/ProtectedRoute';
import { usePersona } from '../../hooks/usePersona';

import Login from '../../pages/Login';
import NotFound from '../../pages/NotFound';
import Profile from '../../pages/Profile';
import AgenticSearch from '../../pages/AgenticSearch';
import AgentPage from '../../pages/AgentPage';
import UsersPage from '../../pages/UsersPage';
import OnboardSite from '../../pages/OnboardSite';
import OnboardingDemo from '../../pages/OnboardingDemo';
import SiteDetailPage from '../../pages/SiteDetailPage';
import SiteRouteSync from '../layout/SiteRouteSync';

// New Carbon shell screens
import HomeAlarms from '../../pages/shell-screens/HomeAlarms';
import AlarmDetail from '../../pages/shell-screens/AlarmDetail';
import SopArtifactDetail from '../../pages/sop/SopArtifactDetail';
import DailyCompliance from '../../pages/shell-screens/DailyCompliance';
import OptimizationsScreen from '../../pages/shell-screens/OptimizationsScreen';
import FloorPlanScreen from '../../pages/shell-screens/FloorPlanScreen';
import LogScreen from '../../pages/shell-screens/LogScreen';
import AuditScreen from '../../pages/shell-screens/AuditScreen';
import Preferences from '../../pages/shell-screens/Preferences';
import SiteKnowledgeGraph from '../../pages/shell-screens/SiteKnowledgeGraph';
import SiteAssets from '../../pages/shell-screens/SiteAssets';
import SiteKnowledgeBase from '../../pages/shell-screens/SiteKnowledgeBase';
import PlanDetail from '../../pages/site-sections/schedule/PlanDetail';
import FleetOverview from '../../pages/shell-screens/FleetOverview';
import Scaffold from '../../pages/shell-screens/Scaffold';
import { useActiveSite } from '../../hooks/useActiveSite';

/** Redirect "/" to the persona's landing screen. */
function PersonaHome() {
  const { persona } = usePersona();
  const to =
    persona === 'executive' ? '/executive' : persona === 'manager' ? '/home/all' : '/home/critical';
  return <Navigate to={to} replace />;
}

/** Redirect "/site" to the user's resolved site (from OMS / /me). */
function SiteRedirect() {
  const { siteId } = useActiveSite();
  return <Navigate to={`/site/${siteId}/kg`} replace />;
}

/**
 * Redirect legacy /site/:id/schedule[/:planId] bookmarks to the Optimizations
 * section, where the plan surface now lives. The plan detail route is keyed
 * by HITL approval ID now, which the legacy date-based :planId can't be
 * mapped to, so any bookmark (bare listing or a specific old plan) falls
 * back to the optimizations list.
 */
function LegacyScheduleRedirect() {
  return <Navigate to="/optimizations" replace />;
}

/**
 * Onboarding screen selector. When the specific GA-10 site is selected, show
 * the GA-10 onboarding demo (document upload + streaming classification);
 * otherwise show the generic new-site wizard.
 */
function OnboardingRouter() {
  const { siteId, isFleet } = useActiveSite();
  return !isFleet && siteId === 'GA-10' ? <OnboardingDemo /> : <OnboardSite />;
}

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <PersonaHome />
          </ProtectedRoute>
        }
      />

      {/* Home / alarms ---------------------------------------------------- */}
      <Route path="/home" element={<Navigate to="/home/critical" replace />} />
      <Route
        path="/home/daily"
        element={
          <ProtectedRoute>
            <DailyCompliance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/home/optimizations"
        element={
          <ProtectedRoute>
            <OptimizationsScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/home/:bucket"
        element={
          <ProtectedRoute>
            <HomeAlarms />
          </ProtectedRoute>
        }
      />
      <Route
        path="/alarms/:id"
        element={
          <ProtectedRoute>
            <AlarmDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sops/:id"
        element={
          <ProtectedRoute>
            <SopArtifactDetail />
          </ProtectedRoute>
        }
      />

      {/* Site ------------------------------------------------------------- */}
      <Route
        path="/site/:site_id/kg"
        element={
          <ProtectedRoute>
            <SiteRouteSync />
            <SiteKnowledgeGraph />
          </ProtectedRoute>
        }
      />
      <Route
        path="/site/:site_id/assets"
        element={
          <ProtectedRoute>
            <SiteRouteSync />
            <SiteAssets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/site/:site_id/floor"
        element={
          <ProtectedRoute>
            <SiteRouteSync />
            <FloorPlanScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/site/:site_id/kb"
        element={
          <ProtectedRoute>
            <SiteRouteSync />
            <SiteKnowledgeBase />
          </ProtectedRoute>
        }
      />
      {/* Legacy schedule bookmarks → Optimizations (must precede the catch-all). */}
      <Route
        path="/site/:site_id/schedule/*"
        element={
          <ProtectedRoute>
            <LegacyScheduleRedirect />
          </ProtectedRoute>
        }
      />
      {/* Legacy site tabs (assets, sop, equipment-docs) reused */}
      <Route
        path="/site/:site_id/*"
        element={
          <ProtectedRoute>
            <SiteRouteSync />
            <SiteDetailPage />
          </ProtectedRoute>
        }
      />
      <Route path="/site" element={<SiteRedirect />} />

      {/* Log / Audit / Preferences --------------------------------------- */}
      <Route
        path="/log"
        element={
          <ProtectedRoute>
            <LogScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit"
        element={
          <ProtectedRoute>
            <AuditScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prefs"
        element={
          <ProtectedRoute>
            <Preferences />
          </ProtectedRoute>
        }
      />

      {/* Manager / Executive (scaffolded) -------------------------------- */}
      <Route
        path="/fleet"
        element={
          <ProtectedRoute>
            <FleetOverview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fleet/sites"
        element={
          <ProtectedRoute>
            <Scaffold
              title="Site detail"
              blurb="Per-site drill-down with the facility power-chain graph. Reuses the digital-twin graph engine."
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fleet/sop"
        element={
          <ProtectedRoute>
            <Scaffold
              title="SOP governance"
              blurb="Coverage, compliance gaps and the SOP register across the fleet."
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fleet/audit"
        element={
          <ProtectedRoute>
            <Scaffold
              title="Helios Audit"
              blurb="System-of-record event timeline and regional posture."
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/optimizations"
        element={
          <ProtectedRoute>
            <OptimizationsScreen review />
          </ProtectedRoute>
        }
      />
      {/* Optimization plan detail — scoped to the Optimizations section (not Site).
          ProtectedRoute (not role-gated) so operators reaching the list via
          /home/optimizations can open a plan too. Keyed by HITL approval ID. */}
      <Route
        path="/optimizations/:approvalId/*"
        element={
          <ProtectedRoute>
            <PlanDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingRouter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/ga10"
        element={
          <ProtectedRoute>
            <OnboardingDemo />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bills"
        element={
          <ProtectedRoute>
            <Scaffold
              title="Bills"
              blurb="Management-only billing surface. Not part of this PR's scope."
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/access"
        element={
          <ProtectedRoute>
            <Scaffold
              title="Access & permissions"
              blurb="Role-assignment management. Not part of this PR's scope."
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/executive"
        element={
          <ProtectedRoute>
            <Scaffold
              title="Fleet posture"
              blurb="Executive portfolio posture — sites healthy, attention, open escalations, compliance."
            />
          </ProtectedRoute>
        }
      />

      {/* Backwards-compatible redirects for old bookmarks ---------------- */}
      <Route path="/admin" element={<Navigate to="/fleet" replace />} />
      <Route path="/admin/users" element={<Navigate to="/users" replace />} />
      <Route path="/admin/onboard" element={<Navigate to="/onboarding" replace />} />
      <Route path="/admin/onboarding-demo" element={<Navigate to="/onboarding/ga10" replace />} />
      <Route path="/admin/audit" element={<Navigate to="/audit" replace />} />
      <Route
        path="/admin/agent"
        element={
          <ProtectedRoute>
            <AgentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/search"
        element={
          <ProtectedRoute>
            <AgenticSearch />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/:id"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
